import React from 'react';
import { useTranslation } from 'react-i18next';

export const useIndustryData = () => {
    const { t } = useTranslation();

    // Helper to safely render bullet points from the JSON arrays
    const renderInfoReason = (translationPath) => {
        const items = t(translationPath, { returnObjects: true });
        if (!Array.isArray(items)) return null;
        return (
            <ul style={{ paddingLeft: '14px', margin: 0, listStyleType: 'disc', textAlign: 'left' }}>
                {items.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: idx === items.length - 1 ? '0' : '4px' }} dangerouslySetInnerHTML={{ __html: item }} />
                ))}
            </ul>
        );
    };

    return [
        {
            id: 'app_fraud',
            title: t('industryApplicationsFinance.data.app_fraud.title'),
            subtitle: t('industryApplicationsFinance.data.app_fraud.subtitle'),
            problem: t('industryApplicationsFinance.data.app_fraud.problem'),
            impact: t('industryApplicationsFinance.data.app_fraud.impact'),
            // TODO: Add a specific logo for this section if desired, e.g., a banking icon or leave blank
            logo: require('./assets/industry/app_fraud.webp'), 
            solutionPoints: [
                { 
                    title: t('industryApplicationsFinance.data.app_fraud.solutions.audioForensics.title'), 
                    text: t('industryApplicationsFinance.data.app_fraud.solutions.audioForensics.text'),
                    renderType: 'audio-player',
                    // ASSETS NEEDED: You will need a background image and a sample audio file.
                    mediaImage: require('./assets/application/app_fraud_0.webp'), 
                    mediaUrl: 'https://vetra-ai-public-content.s3.eu-central-1.amazonaws.com/audio_app_fraud.mp3', // Placeholder URL
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=076d2e4a30cf27b7825eef537174158db16e3d39e321cfe9f0aae9ee1ce82a62&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9',
                    shieldIcon: require('../IndustryApplications/assets/application/vetraai_logo_white.webp'),
                    infoPoint: {
                        id: 'info', position: 'right',
                        title: t('industryApplicationsFinance.data.app_fraud.solutions.audioForensics.infoTitle'),
                        reason: renderInfoReason('industryApplicationsFinance.data.app_fraud.solutions.audioForensics.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsFinance.data.app_fraud.solutions.auditTrail.title'), 
                    text: t('industryApplicationsFinance.data.app_fraud.solutions.auditTrail.text'),
                    renderType: 'chain-of-custody' 
                }
            ]
        },
        {
            id: 'kyc_fraud',
            title: t('industryApplicationsFinance.data.kyc_fraud.title'),
            subtitle: t('industryApplicationsFinance.data.kyc_fraud.subtitle'),
            problem: t('industryApplicationsFinance.data.kyc_fraud.problem'),
            impact: t('industryApplicationsFinance.data.kyc_fraud.impact'),
            logo: require('./assets/industry/kyc_fraud.webp'), 
            solutionPoints: [
                { 
                    title: t('industryApplicationsFinance.data.kyc_fraud.solutions.biometricHeatmaps.title'), 
                    text: t('industryApplicationsFinance.data.kyc_fraud.solutions.biometricHeatmaps.text'),
                    renderType: 'heatmap',
                    mediaImage: require('./assets/application/kyc_fraud_0.webp'), 
                    regions: [
                        { id: 'collar', box: [0.32, 0.73, 0.68, 1.0], reason: t('industryApplicationsFinance.data.kyc_fraud.solutions.biometricHeatmaps.regions.collar'), severity: 9, position: 'top' },
                        { id: 'forehead', box: [0.35, 0.2, 0.65, 0.35], reason: t('industryApplicationsFinance.data.kyc_fraud.solutions.biometricHeatmaps.regions.forehead'), severity: 7, position: 'bottom' },
                        { id: 'hair_edge', box: [0.24, 0.25, 0.3, 0.45], reason: t('industryApplicationsFinance.data.kyc_fraud.solutions.biometricHeatmaps.regions.hair_edge'), severity: 5, position: 'right' }
                    ],
                    infoPoint: {
                        id: 'info', box: [0.92, 0.92, 0.0, 0.0], position: 'top',
                        title: t('industryApplicationsFinance.data.kyc_fraud.solutions.biometricHeatmaps.infoTitle'),
                        reason: renderInfoReason('industryApplicationsFinance.data.kyc_fraud.solutions.biometricHeatmaps.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsFinance.data.kyc_fraud.solutions.automatedTriage.title'), 
                    text: t('industryApplicationsFinance.data.kyc_fraud.solutions.automatedTriage.text'),
                    renderType: 'kyc-pipeline' 
                }
            ]
        },
        {
            id: 'document_forgery',
            title: t('industryApplicationsFinance.data.document_forgery.title'),
            subtitle: t('industryApplicationsFinance.data.document_forgery.subtitle'),
            problem: t('industryApplicationsFinance.data.document_forgery.problem'),
            impact: t('industryApplicationsFinance.data.document_forgery.impact'),
            logo: require('./assets/industry/document_forgery.webp'), 
            solutionPoints: [
                { 
                    title: t('industryApplicationsFinance.data.document_forgery.solutions.tamperingHeatmaps.title'), 
                    text: t('industryApplicationsFinance.data.document_forgery.solutions.tamperingHeatmaps.text'),
                    renderType: 'heatmap',
                    mediaImage: require('./assets/application/document_forgery_0.webp'), 
                    regions: [
                        { id: 'header', box: [0.25, 0.23, 0.75, 0.31], reason: t('industryApplicationsFinance.data.document_forgery.solutions.tamperingHeatmaps.regions.header'), severity: 8, position: 'bottom' },
                        { id: 'table', box: [0.25, 0.37, 0.75, 0.69], reason: t('industryApplicationsFinance.data.document_forgery.solutions.tamperingHeatmaps.regions.table'), severity: 10, position: 'right' }
                    ],
                    infoPoint: {
                        id: 'info', box: [0.92, 0.92, 0.0, 0.0], position: 'top',
                        title: t('industryApplicationsFinance.data.document_forgery.solutions.tamperingHeatmaps.infoTitle'),
                        reason: renderInfoReason('industryApplicationsFinance.data.document_forgery.solutions.tamperingHeatmaps.infoReason')
                    },
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=demo_finance_document_forgery&context=digital_forgery_detection&regions=true&explanation=true',
                    shieldIcon: require('../IndustryApplications/assets/application/vetraai_logo_white.webp'),
                    shieldPosition: 'top-right'
                },
                { 
                    title: t('industryApplicationsFinance.data.document_forgery.solutions.underwritingFilter.title'), 
                    text: t('industryApplicationsFinance.data.document_forgery.solutions.underwritingFilter.text'),
                    renderType: 'loan-pipeline' 
                }
            ]
        },
        {
            id: 'ato_fraud',
            title: t('industryApplicationsFinance.data.ato_fraud.title'),
            subtitle: t('industryApplicationsFinance.data.ato_fraud.subtitle'),
            problem: t('industryApplicationsFinance.data.ato_fraud.problem'),
            impact: t('industryApplicationsFinance.data.ato_fraud.impact'),
            logo: require('./assets/industry/ato_fraud.webp'), 
            solutionPoints: [
                { 
                    title: t('industryApplicationsFinance.data.ato_fraud.solutions.acousticScamDetection.title'), 
                    text: t('industryApplicationsFinance.data.ato_fraud.solutions.acousticScamDetection.text'),
                    renderType: 'audio-player',
                    mediaImage: require('./assets/application/ato_fraud_0.webp'), 
                    mediaUrl: 'https://vetra-ai-public-content.s3.eu-central-1.amazonaws.com/audio_ato_fraud.mp3', // Update this link once you upload to S3
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=32b4ba0ee4553a76acdd2657121dda27e06c7f04dc8922fd5668573e18d1fd9c&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9',
                    shieldIcon: require('../IndustryApplications/assets/application/vetraai_logo_white.webp'),
                    infoPoint: {
                        id: 'info', position: 'right',
                        title: t('industryApplicationsFinance.data.ato_fraud.solutions.acousticScamDetection.infoTitle'),
                        reason: renderInfoReason('industryApplicationsFinance.data.ato_fraud.solutions.acousticScamDetection.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsFinance.data.ato_fraud.solutions.csrAlert.title'), 
                    text: t('industryApplicationsFinance.data.ato_fraud.solutions.csrAlert.text'),
                    renderType: 'call-center-pipeline' 
                }
            ]
        },
        {
            id: 'market_fraud',
            title: t('industryApplicationsFinance.data.market_fraud.title'),
            subtitle: t('industryApplicationsFinance.data.market_fraud.subtitle'),
            problem: t('industryApplicationsFinance.data.market_fraud.problem'),
            impact: t('industryApplicationsFinance.data.market_fraud.impact'),
            logo: require('./assets/industry/market_fraud.webp'), 
            solutionPoints: [
                { 
                    title: t('industryApplicationsFinance.data.market_fraud.solutions.semanticTriage.title'), 
                    text: t('industryApplicationsFinance.data.market_fraud.solutions.semanticTriage.text'),
                    renderType: 'text-highlight', 
                    // No mediaImage needed because it's rendering text
                    // VETRA_AI_IGNORE_NEXT
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=a97d4f77d69b38cc4663372ceeb0ba4d61a79c0f41a1bf42cfb3e198ec00ba23&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9&ctx=0d9967034aff618a1269f70d3adef1f7cbd4ea9954da07acc8a7bb565a087efb', 
                    shieldIcon: require('../IndustryApplications/assets/application/vetraai_logo_white.webp'),
                    textContent: t('industryApplicationsFinance.data.market_fraud.solutions.semanticTriage.textContent'),
                    regions: [
                        { quote: t('industryApplicationsFinance.data.market_fraud.solutions.semanticTriage.regions', { returnObjects: true })[0]?.quote, reason: t('industryApplicationsFinance.data.market_fraud.solutions.semanticTriage.regions', { returnObjects: true })[0]?.reason, severity: 8 },
                        { quote: t('industryApplicationsFinance.data.market_fraud.solutions.semanticTriage.regions', { returnObjects: true })[1]?.quote, reason: t('industryApplicationsFinance.data.market_fraud.solutions.semanticTriage.regions', { returnObjects: true })[1]?.reason, severity: 7 },
                        { quote: t('industryApplicationsFinance.data.market_fraud.solutions.semanticTriage.regions', { returnObjects: true })[2]?.quote, reason: t('industryApplicationsFinance.data.market_fraud.solutions.semanticTriage.regions', { returnObjects: true })[2]?.reason, severity: 9 },
                        { quote: t('industryApplicationsFinance.data.market_fraud.solutions.semanticTriage.regions', { returnObjects: true })[3]?.quote, reason: t('industryApplicationsFinance.data.market_fraud.solutions.semanticTriage.regions', { returnObjects: true })[3]?.reason, severity: 10 }
                    ],
                    infoPoint: {
                        id: 'info', position: 'right',
                        title: t('industryApplicationsFinance.data.market_fraud.solutions.semanticTriage.infoTitle'),
                        reason: renderInfoReason('industryApplicationsFinance.data.market_fraud.solutions.semanticTriage.infoReason')
                    }
                },
                { 
                    title: t('industryApplicationsFinance.data.market_fraud.solutions.crossReference.title'), 
                    text: t('industryApplicationsFinance.data.market_fraud.solutions.crossReference.text'),
                    // Let's reuse the news pipeline animation here, it fits perfectly
                    renderType: 'market-pipeline' 
                }
            ]
        },
        {
            id: 'brand_abuse',
            title: t('industryApplicationsFinance.data.brand_abuse.title'),
            subtitle: t('industryApplicationsFinance.data.brand_abuse.subtitle'),
            problem: t('industryApplicationsFinance.data.brand_abuse.problem'),
            impact: t('industryApplicationsFinance.data.brand_abuse.impact'),
            logo: require('./assets/industry/brand_abuse.webp'), 
            solutionPoints: [
                { 
                    title: t('industryApplicationsFinance.data.brand_abuse.solutions.trademarkScam.title'), 
                    text: t('industryApplicationsFinance.data.brand_abuse.solutions.trademarkScam.text'),
                    renderType: 'heatmap',
                    mediaImage: require('./assets/application/brand_abuse_0.webp'), 
                    regions: [
                        { id: 'logo', box: [0.32, 0.05, 0.68, 0.19], reason: t('industryApplicationsFinance.data.brand_abuse.solutions.trademarkScam.regions.logo'), severity: 8, position: 'right' },
                        { id: 'urgency_text', box: [0.04, 0.22, 0.96, 0.64], reason: t('industryApplicationsFinance.data.brand_abuse.solutions.trademarkScam.regions.urgency_text'), severity: 9, position: 'top' },
                        { id: 'phishing_link', box: [0.12, 0.71, 0.88, 0.84], reason: t('industryApplicationsFinance.data.brand_abuse.solutions.trademarkScam.regions.phishing_link'), severity: 10, position: 'bottom' }
                    ],
                    infoPoint: {
                        id: 'info', box: [0.92, 0.92, 0.0, 0.0], position: 'top',
                        title: t('industryApplicationsFinance.data.brand_abuse.solutions.trademarkScam.infoTitle'),
                        reason: renderInfoReason('industryApplicationsFinance.data.brand_abuse.solutions.trademarkScam.infoReason')
                    },
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=demo_finance_brand_abuse&context=trademark_detection&context=scam_detection&regions=true&explanation=true',
                    shieldIcon: require('../IndustryApplications/assets/application/vetraai_logo_white.webp'),
                    shieldPosition: 'top-right'
                },
                { 
                    title: t('industryApplicationsFinance.data.brand_abuse.solutions.automatedTakedown.title'), 
                    text: t('industryApplicationsFinance.data.brand_abuse.solutions.automatedTakedown.text'),
                    renderType: 'phishing-pipeline' 
                }
            ]
        },
        // We will add Synthetic Identity Fraud (KYC) here next!
    ];
};